/** Pure toast state — split out from `Toast.tsx` (which has JSX) so `node --test` can import it
 * directly with no JSX transform, same pattern as `Dialog.tsx`/`focusTrap.ts`. */

export type ToastVariant = 'success' | 'info' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  variant: ToastVariant;
  title: string;
  description?: string;
  /** Defaults to 4000ms for success/info, 8000ms for error — see `resolveToastDuration`. */
  durationMs?: number;
  /** DESIGN.md's `ToastAction` — e.g. "Undo"/"Redo". */
  action?: ToastAction;
}

export interface Toast extends ToastOptions {
  id: string;
  durationMs: number;
}

export const MAX_VISIBLE_TOASTS = 4;

export type ToastListAction = { type: 'add'; toast: Toast } | { type: 'dismiss'; id: string };

/**
 * Caps the stack at `MAX_VISIBLE_TOASTS`, dropping the OLDEST toast rather than refusing the
 * newest one — a KJ acting fast should always see their most recent action confirmed.
 *
 * MVP-381: "oldest" now means oldest toast WITHOUT an action. A plain toast is a receipt of
 * something already done; a toast with an action is the only place that action can still be taken,
 * and the cut receipt's Undo is the one the KJ is most likely to be reaching for while the three
 * toasts that evicted it were routine confirmations of the very keystrokes they were typing. If
 * every toast on the stack carries an action there is nothing cheaper to drop, so the oldest goes
 * — the cap still holds, and the stack never grows without bound.
 */
export function toastReducer(state: Toast[], action: ToastListAction): Toast[] {
  switch (action.type) {
    case 'add': {
      const next = [...state, action.toast];
      if (next.length <= MAX_VISIBLE_TOASTS) return next;
      const evictIndex = next.findIndex((t) => !t.action);
      next.splice(evictIndex === -1 ? 0 : evictIndex, 1);
      return next;
    }
    case 'dismiss':
      return state.filter((t) => t.id !== action.id);
    default:
      return state;
  }
}

/** The variant->default-duration rule. */
export function resolveToastDuration(variant: ToastVariant, durationMs?: number): number {
  if (durationMs != null) return durationMs;
  return variant === 'error' ? 8000 : 4000;
}
/**
 * MVP-381 pauses the dismissal countdown; MVP-409 bounds the pause. The rule lives here, with the
 * reducer, because `ToastItem` owning it as an inline boolean is how the ceiling went missing in the
 * first place -- the three inputs read as obviously-correct in isolation and only the combination is
 * wrong.
 *
 * `documentHidden` is unbounded on purpose: a toast that burned down while the KJ was in the Stage
 * window was never offered at all, and no ceiling fixes that. `held` IS bounded, because the toast
 * floats over live controls and reaching for one of them is what sets it.
 */
export function isToastPaused({ held, holdExpired, documentHidden }: { held: boolean; holdExpired: boolean; documentHidden: boolean }): boolean {
  return (held && !holdExpired) || documentHidden;
}

/** The ceiling on `held`. Far past any reach for an Undo, far short of "the rest of the night". */
export const MAX_HOLD_MS = 10_000;
